<template>
  <div
    class="route-builder-table-expanded-details-row"
    :class="{
      'three-col': !isShipment,
      'four-col': isShipment,
    }"
  >
    <div class="stop cell">
      <LocationTracker
        :stop-type="shippingPoint.stopType"
        :sequence-number="shippingPoint.sequenceNumber"
        :mileage="mileageForLeg"
        :has-next-stop="hasNextStop"
        :has-error="
          hasNextStop &&
          !mileage.loading &&
          mileage.data &&
          mileageForLeg === undefined
        "
      />

      <!--
      No `label` prop because the overflow styles for the button cause an
      unnecessary tooltip to always display on hover.
      -->
      <KeyValuePair :value="stopTooltip" class="stop-text">
        <template #label="{ loading }">
          <template v-if="loading">
            <Skeleton width="60%" height="1rem" />
            <!--
              Classes and div wrapper make the loading icon same dimensions as
              the button it replaces
              -->
            <div
              aria-hidden="true"
              class="fake p-button p-component p-button-icon-only p-button-text rygen-button rygen-button-tertiary p-button-sm with-icon"
            >
              <i class="pi pi-spinner pi-spin p-button-icon" />
            </div>
          </template>
          <template v-else>
            <div class="flex flex-gap-1 align-items-center">
              {{ stopTypeLabel }}

              <ProposedShipmentIcon
                v-if="
                  shipmentContainingShippingPoint && isTransportSegmentEnabled
                "
                :proposed-shipment="shipmentContainingShippingPoint"
                :title="
                  t('routeBuilder.paramOnProposedShipment', {
                    param: t('shipments.stops'),
                    proposedShipmentIdentifier:
                      shipmentContainingShippingPoint.identifier,
                  })
                "
                class="containing-shipment-icon"
              />
            </div>

            <template
              v-if="
                // VSCode's syntax higlighting gets borked with instanceof in templates
                shipment &&
                (shippingPoint instanceof ProposedShipmentStop ||
                  shippingPoint instanceof RoutePlanStop)
              "
            >
              <RygenButton
                v-if="!readonly && shippingPoint.isPlaceholderStop"
                mode="tertiary"
                :icon="PrimeIcons.PLUS_CIRCLE"
                :aria-label="t('common.addParam', { param: stopTypeLabel })"
                @click="onAddLocationToStop(shippingPoint)"
              />
              <RygenButton
                v-else-if="!readonly"
                mode="tertiary"
                :icon="PrimeIcons.MINUS_CIRCLE"
                :aria-label="
                  t('common.removeParam', {
                    param: t('common.paramNum', {
                      param: t('shipments.stops'),
                      num: shippingPoint.sequenceNumber,
                    }),
                  })
                "
                :severity="Severity.DANGER"
                @click="
                  emit('remove-stop', {
                    stop: shippingPoint,
                    shipmentIdentifier: shipment.identifier,
                    numShipOrdersOnStop: shipOrders.length,
                  })
                "
              />
              <!-- Read-only: no action button, but keep the column width so the
                   stop label stays aligned with the sequence number. -->
              <RygenButton
                v-else
                mode="tertiary"
                :icon="PrimeIcons.PLUS_CIRCLE"
                aria-hidden="true"
                class="opacity-0"
                disabled
              />
            </template>
            <RygenButton
              v-else
              mode="tertiary"
              :icon="PrimeIcons.PLUS_CIRCLE"
              aria-hidden="true"
              class="opacity-0"
              disabled
            />
            <!-- Invisible button keeps label aligned with sequence number -->
          </template>
        </template>

        <template #value>
          <NamedAddress
            v-if="locationName"
            :name="locationName"
            :address="locationAddress"
          />
          <div v-else>
            <div
              class="flex align-items-center"
              :style="{ gap: 'var(--sp-2)' }"
            >
              <i
                aria-hidden="true"
                :class="PrimeIcons.EXCLAMATION_TRIANGLE"
                class="text-warning"
              />
              <span class="font-medium">
                {{ t('common.noParam', { param: t('locations.locations') }) }}
              </span>
            </div>
            <div>-</div>
            <div>-</div>
          </div>
        </template>
      </KeyValuePair>
    </div>

    <div class="appointment cell">
      <PopupDateRange
        v-if="isShipment && isTransportSegmentEnabled && !readonly"
        :start="shippingPoint.requestedAppointmentEarliest"
        :end="shippingPoint.requestedAppointmentLatest"
        :time-zone="shippingPoint.location.timeZone"
        :label="appointmentLabel"
        class="proposed-shipment-appointment"
        @update:start="emit('edit-appointment-start', $event)"
        @update:end="emit('edit-appointment-end', $event)"
        @update:time-zone="emit('edit-appointment-time-zone', $event)"
      >
        <template #placeholder="{ label, placeholderStart, placeholderEnd }">
          <LocationCardDateRange
            :label="label"
            :start="placeholderStart"
            :end="placeholderEnd"
          />
        </template>
      </PopupDateRange>
      <LocationCardDateRange
        v-else
        :label="appointmentLabel"
        :start="shippingPoint.requestedAppointmentEarliest"
        :end="shippingPoint.requestedAppointmentLatest"
      />
    </div>

    <div class="line-items cell">
      <LineItemSummary :line-items="lineItemsForStop" show-handling-units>
        <template
          v-if="shipment && lineItemsForStop.length"
          #count="{ formattedCount }"
        >
          <RygenButton
            mode="link"
            :icon="PrimeIcons.LIST"
            :label="formattedCount"
            class="align-self-start"
            @click="showLineItemsModal"
          />
        </template>
      </LineItemSummary>
    </div>

    <div v-if="shipment" class="ship-orders cell">
      <KeyValuePair :label="t('shipOrders.shipOrders', 2)">
        <template #value>
          <ul role="list" class="ship-order-list">
            <li v-for="shipOrder in shipOrders" :key="shipOrder.compositeId">
              <LabeledIcon icon="pi pi-box" :label="shipOrder.compositeId" />

              <RygenButton
                v-if="!readonly"
                mode="tertiary"
                :icon="PrimeIcons.MINUS_CIRCLE"
                :aria-label="
                  t('common.removeParam', {
                    param: t('shipOrders.shipOrderNumParam', {
                      param: shipOrder.compositeId,
                    }),
                  })
                "
                :severity="Severity.DANGER"
                @click="
                  emit('remove-ship-order', {
                    shipOrder,
                    shipmentIdentifiers: [shipment.identifier],
                  })
                "
              />
            </li>
          </ul>
        </template>
      </KeyValuePair>
    </div>

    <RouteBuilderLineItemsModal
      v-if="shipment && stop"
      v-model:visible="lineItemsModal.visible"
      :ship-orders="shipOrders"
      :line-items="lineItemsModal.lineItems"
      :stop="stop"
      :stop-type-label="stopTypeLabel"
      @remove-ship-order="onRemoveShipOrderFromModal"
    />
  </div>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import { DateTime } from 'luxon';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { Loadable, Severity } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import {
  ShipmentStopType,
  ShipOrder,
  ShipOrderLineItem,
  ShipOrderShippingPoint,
  ShippingPoint,
  StopMileageResponse,
} from '@/model';
import {
  ProposedShipment,
  ProposedShipmentLineItem,
  ProposedShipmentStop,
  RemoveShipOrderFromShipmentPayload,
  RemoveStopFromShipmentPayload,
  RequestAddStopToShipmentPayload,
  StopSelector,
} from '@/model/routeBuilder';
import {
  RoutePlanProposedShipment,
  RoutePlanStop,
} from '@/model/routeBuilder/phase2';

import LineItemSummary from '@/components/lineItem/LineItemSummary.vue';
import LocationCardDateRange from '@/components/location/LocationCardDateRange.vue';
import LocationTracker from '@/components/location/LocationTracker.vue';
import ProposedShipmentIcon from '@/components/routeBuilder/planningPool/ProposedShipmentIcon.vue';
import RouteBuilderLineItemsModal, {
  RemoveShipOrderPayload,
} from '@/components/routeBuilder/RouteBuilderLineItemsModal.vue';
import LabeledIcon from '@/components/ui/LabeledIcon.vue';
import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import PopupDateRange from '@rygen/rygen-ui/components/popup/PopupDateRange.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Skeleton from 'primevue/skeleton';

const props = defineProps({
  entity: {
    type: Object as PropType<
      ShipOrder | ProposedShipment | RoutePlanProposedShipment
    >,
    default: () => new ShipOrder(),
  },
  shippingPoint: {
    type: Object as PropType<ShippingPoint>,
    default: () => new ShipOrderShippingPoint(),
  },
  proposedShipments: {
    type: Array as PropType<ProposedShipment[] | RoutePlanProposedShipment[]>,
    default: () => [],
  },
  mileage: {
    type: Object as PropType<Loadable<StopMileageResponse[]>>,
    default: () => ({ loading: false }),
  },
  hasNextStop: {
    type: Boolean,
    default: () => false,
  },
  readonly: {
    type: Boolean,
    default: () => false,
  },
});
const {
  entity,
  shippingPoint,
  proposedShipments,
  mileage,
  hasNextStop,
  readonly,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'add-stop', payload: RequestAddStopToShipmentPayload): void;
  (event: 'edit-appointment-end', payload?: DateTime): void;
  (event: 'edit-appointment-start', payload?: DateTime): void;
  (event: 'edit-appointment-time-zone', payload?: string): void;
  (
    event: 'remove-ship-order',
    payload: RemoveShipOrderFromShipmentPayload,
  ): void;
  (event: 'remove-stop', payload: RemoveStopFromShipmentPayload): void;
}>();

const isTransportSegmentEnabled = useFlag(
  'route-builder-transport-segment-feat-flag',
);
const { t } = useI18n();

const lineItemsModal = ref({
  visible: false,
  lineItems: [] as ProposedShipmentLineItem[],
});

const locationName = toRef(() => shippingPoint.value.location.locationName);
const locationAddress = toRef(() => shippingPoint.value.location.toAddress());

const isShipment = computed(() => {
  return (
    entity.value instanceof ProposedShipment ||
    entity.value instanceof RoutePlanProposedShipment
  );
});

const shipment = computed(() => {
  if (
    entity.value instanceof ProposedShipment ||
    entity.value instanceof RoutePlanProposedShipment
  ) {
    return entity.value;
  } else {
    return undefined;
  }
});

const stop = computed(() => {
  if (entity.value instanceof ProposedShipment) {
    return shippingPoint.value as ProposedShipmentStop;
  } else if (entity.value instanceof RoutePlanProposedShipment) {
    return shippingPoint.value as RoutePlanStop;
  } else {
    return undefined;
  }
});

const isPickup = computed(
  () => shippingPoint.value.stopType === ShipmentStopType.PICKUP,
);

const sequenceNumber = computed(() => shippingPoint.value.sequenceNumber ?? -1);

const mileageForLeg = computed(() => {
  const stopMileages = mileage.value.data ?? [];
  const mileageResult = stopMileages.find(
    (s) => s.originSequenceNumber === sequenceNumber.value,
  );
  return mileageResult?.mileage;
});

const stopTypeLabel = computed(() => {
  if (entity.value instanceof ShipOrder) {
    return isPickup.value ? t('orders.origin') : t('orders.destination');
  } else if (isPickup.value) {
    return shippingPoint.value.sequenceNumber === 1
      ? t('orders.origin')
      : t('shipments.pickups');
  } else {
    return shippingPoint.value.sequenceNumber === entity.value.stops.length
      ? t('orders.destination')
      : t('shipments.dropoffs');
  }
});

const stopTooltip = computed(() => {
  return `${locationName.value}\n${shippingPoint.value.location.cityState}`;
});

const appointmentLabel = computed(() =>
  t('common.paramDates', {
    param: isPickup.value ? t('shipments.pickups') : t('shipments.dropoffs'),
  }),
);

/**
 * The line items for `entity` (the relevant `ShipOrder` or `ProposedShipment`)
 *
 * This property is kinda redundant but useful for debugging.
 * `ProposedShipment.lineItems` is a getter so it doesn't show up in devtools
 * but this computed property does.
 */
const allLineItems = computed(() => entity.value.lineItems);

const lineItemsForStop: Ref<ShipOrderLineItem[] | ProposedShipmentLineItem[]> =
  computed(() => {
    if (entity.value instanceof ShipOrder) {
      return allLineItems.value;
    }

    const sequenceNumberKey = isPickup.value
      ? 'pickupStopSequenceNumber'
      : 'dropOffStopSequenceNumber';
    return allLineItems.value.filter((l) => {
      return l[sequenceNumberKey] === sequenceNumber.value;
    });
  });

const shipOrders = computed(() => {
  if (entity.value instanceof ShipOrder) {
    return [];
  }

  const { sequenceNumber, stopType } = shippingPoint.value;
  const stop = entity.value.stops.find(
    (stop) => stop.sequenceNumber === sequenceNumber,
  );
  if (!stop) {
    console.error(
      `Could not find stop ${sequenceNumber} for proposed shipment ${entity.value.identifier}`,
    );
    return [];
  }

  if (RoutePlanProposedShipment.isRoutePlanProposedShipment(entity.value)) {
    const routePlanProposedShipment = entity.value;
    return routePlanProposedShipment.transportSegments
      .filter((ts) => {
        const sequenceNumberKey =
          stopType === ShipmentStopType.PICKUP
            ? 'pickupStopSequenceNumber'
            : 'dropOffStopSequenceNumber';
        return ts[sequenceNumberKey] === stop.sequenceNumber;
      })
      .map((ts) => ts.shipOrder)
      .filter(notNull);
  } else {
    const proposedShipmentStop = stop as ProposedShipmentStop;
    return entity.value.shipOrders.filter((so) => {
      const shipOrderShippingPoint =
        stopType === ShipmentStopType.PICKUP ? so.origin : so.destination;
      return proposedShipmentStop.hasShipOrderShippingPoint(
        shipOrderShippingPoint,
      );
    });
  }
});

const shipmentsContainingShipOrder = computed(() => {
  const shipOrderOrShipment = entity.value;
  return shipOrderOrShipment instanceof ShipOrder
    ? proposedShipments.value.filter((s) => s.hasShipOrder(shipOrderOrShipment))
    : undefined;
});

const shipmentContainingShippingPoint = computed(() => {
  if (!ShipOrderShippingPoint.isShipOrderShippingPoint(shippingPoint.value)) {
    return undefined;
  }
  const shipOrderShippingPoint = shippingPoint.value;

  return shipmentsContainingShipOrder.value?.find((s) => {
    if (ProposedShipmentStop.isProposedShipmentStop(s.stops[0])) {
      // Phase 1
      const proposedShipment = s as ProposedShipment;
      const stops = proposedShipment.stops;
      const shipmentStopContainingShippingPoint = stops.find((shipmentStop) =>
        shipmentStop.hasShipOrderShippingPoint(shipOrderShippingPoint),
      );
      return !!shipmentStopContainingShippingPoint;
    } else {
      // Phase 2
      const shipOrder = entity.value as ShipOrder;
      const proposedShipment = s as RoutePlanProposedShipment;
      return proposedShipment.hasShipOrderShippingPoint(
        shipOrderShippingPoint,
        shipOrder,
      );
    }
  });
});

const showLineItemsModal = () => {
  // `lineItemsForStop` should always be `ProposedShipmentLineItem[]` when
  // `entity` is a `ProposedShipment` but Typescript seems to have a bug with
  // unions of two different array types
  lineItemsModal.value.lineItems =
    lineItemsForStop.value as ProposedShipmentLineItem[];
  lineItemsModal.value.visible = true;
};

const onAddLocationToStop = (stop: ProposedShipmentStop | RoutePlanStop) => {
  let stopSelector: StopSelector;
  if (stop.sequenceNumber === 1) {
    stopSelector = 'origin';
  } else if (stop.sequenceNumber === shipment.value?.stops.length) {
    stopSelector = 'destination';
  } else {
    stopSelector = 'intermediate';
  }

  emit('add-stop', {
    stopType: stop.stopType,
    stopSelector,
    sequenceNumber: stop.sequenceNumber,
    shipmentIdentifier: stop.shipmentIdentifier,
  });
};

const onRemoveShipOrderFromModal = ({ shipOrder }: RemoveShipOrderPayload) => {
  if (!shipment.value) {
    console.warn('No proposed shipment! Howd we get here?');
    return;
  }

  const isLastShipOrder = shipOrders.value.length === 1;
  const onConfirm = isLastShipOrder
    ? () => (lineItemsModal.value.visible = false)
    : undefined;

  emit('remove-ship-order', {
    shipOrder,
    shipmentIdentifiers: [shipment.value.identifier],
    onConfirm,
  });
};
</script>

<style>
.route-builder-table-expanded-details-row {
  display: contents;

  /* first row / origin */
  &:first-of-type {
    .cell {
      padding-block: 0 var(--sp-3);
    }
  }

  /* last row / destination */
  &:last-of-type {
    .cell {
      padding-block: var(--sp-3) 0;
    }
  }

  &:not(:first-of-type) {
    &:not(:last-of-type) {
      .cell {
        padding-block: var(--sp-3);
      }
    }

    .cell {
      &:not(.stop) {
        border-top: 1px solid var(--divider-color);
      }
    }
  }

  .cell {
    /* last cell; line items for ship order table, ship orders for shipments table */
    &:last-of-type {
      margin-right: var(--sp-6);
    }

    .key-value-pair {
      .key-value-pair-label {
        font-size: 1rem;
      }
    }

    &.stop {
      display: flex;
      gap: var(--gap-1);
      border-right: 1px solid var(--divider-color);
      padding-inline: 0 var(--sp-6);
      margin-inline: 0 var(--sp-6);

      .location-tracker {
        align-items: end;

        .info-icon-container {
          height: 50%;
        }
      }

      .stop-text {
        gap: var(--sp-1);
        width: 100%;

        .key-value-pair-label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--gap-1);
          /*
          The margin top on the button makes its padding overflow so make it
          visible when its hovered
          */
          overflow: visible;

          .rygen-button {
            &.fake {
              /*
              this element is a spinner with the strucutre of a button so it has
              the dimensions of a button, but we want it to look and behave like
              a boring spinner, i.e. no color, hover state, etc. This prevents
              layout from shifting when replacing the loading spinner with the
              remove button. !important in all properties below so that nothing
              can override this
              */
              background: transparent !important;
              border-color: transparent !important;
              color: inherit !important;
            }
          }
        }

        .named-address__name {
          font-weight: initial;
        }
      }
    }

    &.appointment {
      .proposed-shipment-appointment {
        .popup-inputgroup-card {
          width: max-content;

          .p-card-body {
            .p-card-content {
              .popup-inputgroup-fields {
                flex-direction: column;
              }
            }
          }
        }
      }
    }

    &.line-items {
      padding-inline: var(--sp-6);
    }

    &.ship-orders {
      .ship-order-list {
        display: flex;
        flex-flow: column;
        gap: var(--sp-1);
        list-style: none;
        margin: 0;
        padding: 0;

        li {
          display: flex;
          gap: var(--sp-1);

          &:not(:first-of-type) {
            i {
              opacity: 0;
            }
          }

          .labeled-icon {
            flex: 1 0 50%;
          }
        }
      }
    }
  }
}
</style>
